import {
  PrefixedUserDocumentCategoryIdentifier,
  UserDocumentCategoryIdentifier,
} from '@/features/admin/user-document-categories/userDocumentCategory.constants'
import { UploadDocumentType } from '@/constants'
import { UserProcessingFee } from '@/features/bookkeeping/end-of-year-review/actions'

export interface IDocument {
  categoryName: string
  description: string
  instructionsLink?: string
  type: UploadDocumentType
}

export interface IMerchant {
  id: number
  merchantName: string
  documents: IDocument[]
}

export interface IMerchantDocument {
  id: number
  merchantName: string
  documentCategoryName: string
  documentName: string
  documentCategoryNameInternal?: string
  type: UploadDocumentType
}

const { paymentProcessorReports } = PrefixedUserDocumentCategoryIdentifier
const { form1099k } = UserDocumentCategoryIdentifier

// Would be nice to unify this with root documents-map
const getMerchants = (taxYear: string): IMerchant[] => {
  return [
    {
      id: 2,
      merchantName: 'Alma',
      documents: [
        {
          categoryName: paymentProcessorReports(taxYear),
          description: 'Payroll Report or Invoice Summary',
          // This is normally a bookkeeping category but we want the 2 values to match so they can be combined in the flow and uploaded to taxfyle
          type: UploadDocumentType.BOOKKEEPING,
        },
      ],
    },
    {
      id: 3,
      merchantName: 'CardConnect',
      documents: [
        {
          categoryName: paymentProcessorReports(taxYear),
          description: 'Merchant Statement',
          instructionsLink:
            'https://support.joinheard.com/hc/en-us/articles/23140064258967',
          type: UploadDocumentType.BOOKKEEPING,
        },
      ],
    },
    {
      id: 4,
      merchantName: 'Cash App',
      documents: [
        {
          categoryName: form1099k,
          description: '1099-K',
          instructionsLink:
            'https://support.joinheard.com/hc/en-us/articles/28474678568087',
          type: UploadDocumentType.TAX,
        },
      ],
    },
    {
      id: 6,
      merchantName: 'Headway',
      documents: [
        {
          categoryName: form1099k,
          description: '1099-K',
          instructionsLink:
            'https://support.joinheard.com/hc/en-us/articles/28595935498391',
          type: UploadDocumentType.TAX,
        },
      ],
    },
    {
      id: 8,
      merchantName: 'Ivy Pay',
      documents: [
        {
          categoryName: paymentProcessorReports(taxYear),
          description: 'Transactions Activity Report',
          instructionsLink:
            'https://support.joinheard.com/hc/en-us/articles/23140064258967',
          type: UploadDocumentType.BOOKKEEPING,
        },
      ],
    },
    {
      id: 9,
      merchantName: 'Paypal',
      documents: [
        {
          categoryName: form1099k,
          description: '1099-K',
          instructionsLink:
            'https://support.joinheard.com/hc/en-us/articles/28473243834775',
          type: UploadDocumentType.TAX,
        },
      ],
    },
    {
      id: 11,
      merchantName: 'SimplePractice',
      documents: [
        {
          categoryName: paymentProcessorReports(taxYear),
          description: 'Annual Financial Activity Report',
          instructionsLink:
            'https://support.joinheard.com/hc/en-us/articles/28465857710359',
          type: UploadDocumentType.BOOKKEEPING,
        },
      ],
    },
    {
      id: 12,
      merchantName: 'Square',
      documents: [
        {
          categoryName: paymentProcessorReports(taxYear),
          description: 'Sale Summary Report',
          instructionsLink:
            'https://support.joinheard.com/hc/en-us/articles/28465856207639',
          type: UploadDocumentType.BOOKKEEPING,
        },
      ],
    },
    {
      id: 13,
      merchantName: 'Stripe',
      documents: [
        {
          categoryName: paymentProcessorReports(taxYear),
          description: 'Balance Report',
          instructionsLink:
            'https://support.joinheard.com/hc/en-us/articles/28465779444247',
          type: UploadDocumentType.BOOKKEEPING,
        },
        {
          categoryName: paymentProcessorReports(taxYear),
          description: 'Payout Reconciliation Report',
          instructionsLink:
            'https://support.joinheard.com/hc/en-us/articles/28465788364439',
          type: UploadDocumentType.BOOKKEEPING,
        },
      ],
    },
    {
      id: 14,
      merchantName: 'TheraNest',
      documents: [
        {
          categoryName: paymentProcessorReports(taxYear),
          description: 'Vantage Payment Report',
          instructionsLink:
            'https://support.joinheard.com/hc/en-us/articles/28473072194455',
          type: UploadDocumentType.BOOKKEEPING,
        },
      ],
    },
    {
      id: 15,
      merchantName: 'Venmo',
      documents: [
        {
          categoryName: form1099k,
          description: '1099-K',
          instructionsLink:
            'https://support.joinheard.com/hc/en-us/articles/28474488358551',
          type: UploadDocumentType.TAX,
        },
      ],
    },
  ]
}

export const getMerchantNames = (): string[] => {
  return getMerchants('').map(({ merchantName }) => merchantName.toLowerCase())
}

export const getMatchingMerchants = (
  taxYear: string,
  merchantRollUps: UserProcessingFee[],
  toMergeMerchantNames?: string[]
): IMerchantDocument[] => {
  let isAddedToAlma = false
  let isAddedToHeadWay = false

  const filteredMerchants = getMerchants(taxYear).filter((merchant) =>
    merchantRollUps.some(
      (merchantRollUp) =>
        merchantRollUp.merchantName?.toLowerCase() ===
        merchant.merchantName.toLowerCase()
    )
  )

  const merchants = filteredMerchants.flatMap((merchant) =>
    merchant.documents.map((document) => ({
      id: merchant.id,
      merchantName: merchant.merchantName,
      documentCategoryName: document.categoryName,
      documentName: document.description,
      type: document.type,
    }))
  )

  if (toMergeMerchantNames && toMergeMerchantNames.length > 0) {
    const uniqueMerchantsFilter: IMerchantDocument[] = []
    for (const merchant of merchants) {
      if (toMergeMerchantNames.includes(merchant.merchantName)) {
        if (merchant.merchantName === 'Alma') {
          if (isAddedToAlma) continue
          uniqueMerchantsFilter.push({
            ...merchant,
            documentName: 'Payroll Report or Invoice Summary',
            documentCategoryName: 'mix_documents',
            documentCategoryNameInternal: merchant.documentCategoryName,
            type: merchant.type,
          })
          isAddedToAlma = true
        }
        if (merchant.merchantName === 'Headway') {
          if (isAddedToHeadWay) continue
          uniqueMerchantsFilter.push({
            ...merchant,
            documentName: '1099-K',
            documentCategoryName: 'mix_documents',
            documentCategoryNameInternal: merchant.documentCategoryName,
            type: merchant.type,
          })
          isAddedToHeadWay = true
        }
      } else {
        uniqueMerchantsFilter.push(merchant)
      }
    }
    return uniqueMerchantsFilter
  } else {
    return merchants
  }
}

export const getInstructionsLink = (
  categoryName: string,
  merchantName: string,
  taxYear: string
): string => {
  for (const merchant of getMerchants(taxYear)) {
    if (merchant.merchantName === merchantName) {
      const matchingDoc = merchant.documents.find(
        (doc) => doc.categoryName === categoryName
      )

      return matchingDoc?.instructionsLink ?? ''
    }
  }

  return ''
}
